import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Brand context — selected brand for non-full users.
 *
 * Full users (admin/master) keep DashboardPage as `/` and ignore this state
 * for routing. Non-full users get `/` redirected to `/brand/:selectedBrandId`
 * by OnboardingGate. The TopBar WorkspaceSwitcher writes here for everyone.
 *
 * Persisted under `xotski.brandContext` (separate key from auth/sidebar).
 */
/**
 * De dónde vino el último cambio.
 *
 *  · `user`   — la persona eligió una marca (selector, árbol lateral): SE GUARDA
 *               en el servidor para que le siga a cualquier dispositivo (095).
 *  · `server` — se hidrató desde `GET /profile.preferences.lastBrandId`.
 *  · `repair` — la puerta de entrada corrigió un id que ya no es visible.
 *  · `local`  — lo que había en localStorage al arrancar.
 *
 * Sin esto, hidratar desde el servidor volvería a escribir en el servidor, y
 * la corrección automática pisaría en cada dispositivo la elección real.
 */
export type BrandSelectionOrigin = 'user' | 'server' | 'repair' | 'local';

interface BrandContextState {
  selectedBrandId: number | null;
  origin: BrandSelectionOrigin;
  /** Para qué usuario se hidrató ya desde el servidor. `null` = todavía no. */
  hydratedFor: number | null;
  setSelectedBrandId: (id: number | null, origin?: BrandSelectionOrigin) => void;
  markHydrated: (userId: number) => void;
  clear: () => void;
}

export const useBrandContextStore = create<BrandContextState>()(
  persist(
    (set) => ({
      selectedBrandId: null,
      origin: 'local',
      hydratedFor: null,
      setSelectedBrandId: (id, origin = 'user') => set({ selectedBrandId: id, origin }),
      markHydrated: (userId) => set({ hydratedFor: userId }),
      clear: () => set({ selectedBrandId: null, origin: 'local', hydratedFor: null }),
    }),
    {
      name: 'xotski.brandContext',
      partialize: (s) => ({ selectedBrandId: s.selectedBrandId }),
    },
  ),
);

export function useSelectedBrandId(): number | null {
  return useBrandContextStore((s) => s.selectedBrandId);
}
